export interface HeuristicLine {
    text: string;
    isHeading?: boolean;
    isBold?: boolean;
}

const DEFAULT_MAX_LOOK_BACK = 40;
const MAX_SECTION_LENGTH = 100;
// A numbered line longer than this is prose that happens to start with a clause number, so only the
// number is worth keeping. Shorter ones read as a real heading and are kept whole.
const NUMBERED_HEADING_MAX_LENGTH = 60;
const MAX_ALL_CAPS_HEADING_LENGTH = 60;
const MAX_BOLD_HEADING_LENGTH = 80;

const MARKDOWN_HEADING = /^#{1,6}\s+(.+)$/;
const NUMBERED_HEADING = /^(\d+(?:\.\d+)*)[.)]?\s+\S/;
const PARAGRAPH_SIGN_HEADING = /^§+\s*(\d+(?:\.\d+)*)/;
const KEYWORD_HEADING = /^(?:Article|Artikel|Section|Clause|Chapter|Kapitel|Afsnit|Punkt)\s+\d+(?:\.\d+)*/i;
const PAGE_MARKER = /^page\s+\d+$/i;
const PURE_NUMBER = /^\d+(?:[.)]|\.\d+)*[.)]?$/;
const IDENTIFIER_LIKE = /^\S*\d\S*$/;

function toHeuristicLine(line: string | HeuristicLine): HeuristicLine {
    return typeof line === "string" ? { text: line } : line;
}

function isSkippable(text: string): boolean {
    // A `|` means the renderer laid the row out as a table, where the "heading" is a neighbouring cell
    // rather than anything above the selection.
    return text.length === 0 || PAGE_MARKER.test(text) || PURE_NUMBER.test(text) || text.includes("|");
}

function isAllCapsHeading(text: string): boolean {
    return (
        text.length <= MAX_ALL_CAPS_HEADING_LENGTH &&
        /\p{L}/u.test(text) &&
        text === text.toUpperCase() &&
        // A single all-caps token carrying digits is a document id or product code ("DOC-ECHO-2024"),
        // not the name of a section. A real all-caps heading with a number in it has a space
        // ("APPENDIX 2"), so requiring whitespace is enough to tell them apart.
        !IDENTIFIER_LIKE.test(text)
    );
}

function trimToSection(text: string): string {
    const withoutTrailingColon = text.replace(/:$/, "").trim();
    return withoutTrailingColon.length > MAX_SECTION_LENGTH ? withoutTrailingColon.substring(0, MAX_SECTION_LENGTH).trim() : withoutTrailingColon;
}

/**
 * The section this line names, or undefined when the line is not heading-like. Exported for the tests
 * that pin each accept rule down.
 */
export function classifyHeadingLine(line: string | HeuristicLine): string | undefined {
    const { text: rawText, isHeading, isBold } = toHeuristicLine(line);
    const text = rawText.trim();

    if (isSkippable(text)) {
        return undefined;
    }

    const markdown = MARKDOWN_HEADING.exec(text);
    if (markdown) {
        return trimToSection(markdown[1]);
    }

    if (isHeading) {
        return trimToSection(text);
    }

    const numbered = NUMBERED_HEADING.exec(text);
    if (numbered) {
        return text.length > NUMBERED_HEADING_MAX_LENGTH ? numbered[1] : trimToSection(text);
    }

    const paragraphSign = PARAGRAPH_SIGN_HEADING.exec(text);
    if (paragraphSign) {
        return text.length > NUMBERED_HEADING_MAX_LENGTH ? `§ ${paragraphSign[1]}` : trimToSection(text);
    }

    if (KEYWORD_HEADING.test(text)) {
        return trimToSection(text);
    }

    if (isAllCapsHeading(text)) {
        return trimToSection(text);
    }

    if (isBold && text.length <= MAX_BOLD_HEADING_LENGTH) {
        return trimToSection(text);
    }

    return undefined;
}

/**
 * Best-effort guess at which section of a document a selection sits in, by walking backwards from the
 * selected line to the nearest heading-like line. Starts at the selected line itself, so selecting inside
 * "12.3 The Supplier shall deliver" yields "12.3". Undefined when nothing within reach looks like a
 * heading, which leaves the user to type the section themselves.
 */
export function findPrecedingSectionHeading(
    lines: Array<string | HeuristicLine>,
    startLineIndex: number,
    maxLookBack: number = DEFAULT_MAX_LOOK_BACK
): string | undefined {
    const firstIndex = Math.max(0, startLineIndex - maxLookBack);

    for (let index = Math.min(startLineIndex, lines.length - 1); index >= firstIndex; index--) {
        const section = classifyHeadingLine(lines[index]);
        if (section) {
            return section;
        }
    }

    return undefined;
}
